/**
 * PATH: packages/ui/src/kanban/undo/UndoContext.tsx
 * FILE_ID: UI-KANBAN-UNDO-CTX-001
 * PURPOSE: React context that carries the board's `registerUndo` into
 *          subtrees that cannot receive it as a threaded prop — chiefly the
 *          card detail drawer, which hosts mount through `cardDetailsSlot`.
 * WHY: ARC-8 MR2 — extending Ctrl/Cmd+Z coverage to label (tag) toggles.
 *      The drawer's sections are rendered via a host-provided render slot,
 *      so prop-threading from useKanbanBoardView is impossible without
 *      changing every tenant host. Context keeps the host contract intact.
 * FLOW:
 *   ┌────────────────────┐   ┌──────────────────┐   ┌──────────────────┐
 *   │ KanbanBoardView    │ → │ UndoRegister     │ → │ useRegisterUndo()│
 *   │ (boardUndo owner)  │   │ Context.Provider │   │ in drawer section│
 *   └────────────────────┘   └──────────────────┘   └──────────────────┘
 * DEPENDENCIES:
 *   - ./undoStack (UndoEntry): the entry shape being registered.
 * RELATED:
 *   - components/board-view/useBoardUndo.ts: produces the provided value
 *   - components/card-detail/TagsSection.tsx: first context consumer
 *   - undo/coverage.ts: the revertibility coverage table
 */
import { createContext, useContext } from 'react'

import type { UndoEntry } from './undoStack'

export type RegisterUndoFn = (entry: UndoEntry) => void

/** Null outside a board surface (e.g. drawer opened from search/peek) —
 *  consumers treat null as "this action doesn't register for Ctrl+Z". */
export const UndoRegisterContext = createContext<RegisterUndoFn | null>(null)

/** f() -> the board's registerUndo, or null when no board undo stack is
 *  in scope. Never throws — undo coverage degrades gracefully. */
export function useRegisterUndo(): RegisterUndoFn | null {
  return useContext(UndoRegisterContext)
}
